import { useCallback, useEffect, useState } from 'react';
import {
  CloudSun, AlertTriangle, CheckCircle2, HelpCircle, Plus, Trash2, Save, X, History, Pencil,
} from 'lucide-react';
import Button from '../ui/Button';
import sprayService from '../../services/spray';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import {
  WEATHER_METRICS, WEATHER_METRIC_KEYS, describeLimit, formatWeatherValue, sourceLabel,
} from '../../utils/sprayWeatherLimits';
import type {
  SprayWeatherLimits, WeatherLimitBound, WeatherLimitInput, WeatherLimitMetric,
  WeatherLimitPreset, WeatherLimitResult,
} from '../../types/spray';

interface WeatherLimitsCardProps {
  sprayId: number;
  status: string;
  /** Creator/admin on a spray that has not finished — may set the limits. */
  canEdit: boolean;
}

/** An editor row. Numbers stay strings until save so a half-typed "1" is not coerced. */
interface DraftLimit {
  metric: WeatherLimitMetric;
  bound: WeatherLimitBound;
  threshold: string;
  window_hours: string;
}

function toDraft(l: WeatherLimitInput): DraftLimit {
  return {
    metric: l.metric,
    bound: l.bound,
    threshold: String(l.threshold),
    window_hours: l.window_hours != null ? String(l.window_hours) : '24',
  };
}

function fromDraft(d: DraftLimit): WeatherLimitInput {
  return {
    metric: d.metric,
    bound: d.bound,
    threshold: Number(d.threshold),
    window_hours: WEATHER_METRICS[d.metric].kind === 'forecast' ? Number(d.window_hours) : null,
  };
}

function draftProblem(rows: DraftLimit[]): string | null {
  const seen = new Set<string>();
  for (const r of rows) {
    const def = WEATHER_METRICS[r.metric];
    if (r.threshold.trim() === '' || !Number.isFinite(Number(r.threshold))) return `${def.label}: enter a number`;
    if (def.kind === 'forecast') {
      const w = Number(r.window_hours);
      if (!Number.isInteger(w) || w < 1 || w > 168) return `${def.label}: look ahead 1–168 hours`;
    }
    const key = `${r.metric}:${r.bound}`;
    if (seen.has(key)) return `${def.label} has two ${r.bound} limits`;
    seen.add(key);
  }
  return null;
}

function ResultRow({ r, timezone }: { r: WeatherLimitResult; timezone: string }) {
  const colour =
    r.status === 'breach' ? 'text-red-700'
    : r.status === 'ok' ? 'text-green-700'
    : 'text-neutral-400';
  const Icon = r.status === 'breach' ? AlertTriangle : r.status === 'ok' ? CheckCircle2 : HelpCircle;
  const src = sourceLabel(r.source);
  return (
    <li className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-neutral-800">{r.label}</p>
        <p className="text-xs text-neutral-500">
          {describeLimit(r)}
          {src && <> · {src}</>}
          {r.status === 'breach' && r.forecast_at && <> · {formatDateTimeInTz(r.forecast_at, timezone)}</>}
        </p>
      </div>
      <div className={`flex shrink-0 items-center gap-1 text-sm font-semibold ${colour}`}>
        <Icon className="h-4 w-4" />
        {r.status === 'unknown' ? 'No reading' : formatWeatherValue(r.value, r.unit)}
      </div>
    </li>
  );
}

/**
 * The weather this spray may run in — "max gusts 15 km/h", "max forecast
 * temperature 30 °C over the next 72 h", "Delta T 2–8" — and how each limit
 * stands right now (or, for a spray scheduled later, in the forecast for its
 * scheduled start).
 *
 * The server does the judging (sprayWeatherLimitService) and this card only
 * shows its verdict, so what the card says and what the Start gate refuses
 * cannot disagree. It also monitors every 15 minutes and notifies the operator
 * and creator; the card is for the person looking at the spray.
 *
 * "No reading" is its own state, grey, never green: a limit the weather could
 * not be checked against has not been met.
 */
function WeatherLimitsCard({ sprayId, status, canEdit }: WeatherLimitsCardProps) {
  const timezone = useOrgTimezone();
  const [data, setData] = useState<SprayWeatherLimits | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DraftLimit[]>([]);
  const [presets, setPresets] = useState<WeatherLimitPreset[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [presetName, setPresetName] = useState('');
  const [presetMessage, setPresetMessage] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const finished = status === 'completed' || status === 'cancelled';
  const editable = canEdit && !finished;

  const load = useCallback(async () => {
    try {
      const res = await sprayService.getWeatherLimits(sprayId);
      if (res.success && res.data) {
        setData(res.data);
        setLoadFailed(false);
      }
    } catch {
      setLoadFailed(true);
    }
  }, [sprayId]);

  useEffect(() => { void load(); }, [load]);

  // A running spray re-checks on the same cadence as the live weather panel.
  useEffect(() => {
    if (status !== 'in_progress' && status !== 'paused') return;
    const t = setInterval(() => { void load(); }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [status, load]);

  const openEditor = async () => {
    setDraft((data?.limits ?? []).map(toDraft));
    setError(null);
    setPresetMessage(null);
    setEditing(true);
    try {
      const res = await sprayService.getWeatherLimitPresets();
      if (res.success && res.data) setPresets(res.data);
    } catch { /* presets are a convenience; the editor works without them */ }
  };

  const updateRow = (i: number, patch: Partial<DraftLimit>) => {
    setDraft(rows => rows.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, ...patch };
      // A metric change can leave a bound the new metric does not allow.
      const bounds = WEATHER_METRICS[next.metric].bounds;
      if (!bounds.includes(next.bound)) next.bound = bounds[0];
      return next;
    }));
  };

  const addRow = () => {
    const used = new Set(draft.map(d => `${d.metric}:${d.bound}`));
    const metric = WEATHER_METRIC_KEYS.find(m => WEATHER_METRICS[m].bounds.some(b => !used.has(`${m}:${b}`))) ?? 'wind_speed';
    const bound = WEATHER_METRICS[metric].bounds.find(b => !used.has(`${metric}:${b}`)) ?? 'max';
    setDraft(rows => [...rows, { metric, bound, threshold: '', window_hours: '24' }]);
  };

  const applyPreset = (id: string) => {
    const preset = presets.find(p => String(p.id) === id);
    if (!preset) return;
    setDraft(preset.limits.map(toDraft));
    setPresetMessage(`Applied “${preset.name}” — save to use it on this spray.`);
  };

  const save = async () => {
    const problem = draftProblem(draft);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await sprayService.saveWeatherLimits(sprayId, draft.map(fromDraft));
      if (res.success && res.data) {
        setData(res.data);
        setEditing(false);
      }
    } catch (err) {
      setError((err as Error)?.message || 'Could not save the weather limits');
    } finally {
      setSaving(false);
    }
  };

  const saveAsPreset = async () => {
    const problem = draftProblem(draft);
    if (problem) { setError(problem); return; }
    if (!draft.length) { setError('Add at least one limit first'); return; }
    const name = presetName.trim();
    if (!name) { setError('Give the preset a name'); return; }
    const existing = presets.find(p => p.name.toLowerCase() === name.toLowerCase());
    setError(null);
    try {
      const body = { name, limits: draft.map(fromDraft) };
      const res = existing
        ? await sprayService.updateWeatherLimitPreset(existing.id, { ...body, description: existing.description })
        : await sprayService.createWeatherLimitPreset(body);
      if (res.success && res.data) {
        const saved = res.data;
        setPresets(ps => [...ps.filter(p => p.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name)));
        setPresetName('');
        setPresetMessage(existing ? `Updated preset “${saved.name}”.` : `Saved preset “${saved.name}”.`);
      }
    } catch (err) {
      setError((err as Error)?.message || 'Could not save the preset');
    }
  };

  const check = data?.check ?? null;
  const limits = data?.limits ?? [];
  const breachCount = check?.results.filter(r => r.status === 'breach').length ?? 0;

  // Nothing set and nobody here who could set any — the card has nothing to say.
  if (!editable && !limits.length && !(data?.breaches.length)) return null;

  return (
    <div className={`rounded-xl border p-5 space-y-3 ${
      breachCount ? 'border-red-300 bg-red-50' : 'border-neutral-200 bg-white'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <CloudSun className="h-5 w-5 shrink-0 text-primary-600" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-600">Weather limits</h3>
            {check && (
              <p className="text-xs text-neutral-500">
                {check.anchor === 'scheduled'
                  ? `Forecast for the scheduled start, ${formatDateTimeInTz(check.anchor_at, timezone)}`
                  : `Conditions now · checked ${formatDateTimeInTz(check.checked_at, timezone)}`}
              </p>
            )}
          </div>
        </div>
        {editable && !editing && (
          <Button variant="outline" size="sm" onClick={openEditor}>
            {limits.length ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {limits.length ? 'Edit' : 'Set limits'}
          </Button>
        )}
      </div>

      {loadFailed && !data && (
        <p className="text-sm text-neutral-500">Could not load the weather limits.</p>
      )}

      {/* ── Verdict ─────────────────────────────────────────────────────── */}
      {!editing && limits.length > 0 && (
        <>
          {breachCount > 0 && (
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <p className="text-sm text-red-800">
                <strong>
                  {breachCount === 1 ? 'One limit is' : `${breachCount} limits are`} outside what this spray allows.
                </strong>{' '}
                {status === 'in_progress'
                  ? 'Consider pausing until conditions settle.'
                  : status === 'paused'
                    ? 'Wait for conditions to settle before resuming.'
                    : 'Starting will ask the operator to confirm.'}
              </p>
            </div>
          )}
          {check ? (
            <ul className="divide-y divide-neutral-100">
              {check.results.map(r => <ResultRow key={`${r.metric}:${r.bound}`} r={r} timezone={timezone} />)}
            </ul>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {limits.map(l => (
                <li key={l.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="text-sm text-neutral-800">{WEATHER_METRICS[l.metric]?.label ?? l.metric}</span>
                  <span className="text-xs text-neutral-500">{describeLimit(l)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {!editing && !limits.length && editable && (
        <p className="text-sm text-neutral-500">
          No limits set. Add the conditions this spray must not run in — wind, gusts, temperature,
          Delta T, rain — and the operator and you are notified if the weather goes outside them.
        </p>
      )}

      {/* ── Editor ──────────────────────────────────────────────────────── */}
      {editing && (
        <div className="space-y-3">
          {presets.length > 0 && (
            <select
              aria-label="Apply a preset"
              value=""
              onChange={e => applyPreset(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">Apply a preset…</option>
              {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}

          {draft.map((row, i) => {
            const def = WEATHER_METRICS[row.metric];
            return (
              <div key={i} className="space-y-2 rounded-lg border border-neutral-200 bg-white p-3">
                <div className="flex items-center gap-2">
                  <select
                    aria-label="Metric"
                    value={row.metric}
                    onChange={e => updateRow(i, { metric: e.target.value as WeatherLimitMetric })}
                    className="min-w-0 flex-1 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
                  >
                    {WEATHER_METRIC_KEYS.map(m => <option key={m} value={m}>{WEATHER_METRICS[m].label}</option>)}
                  </select>
                  <button
                    type="button"
                    aria-label="Remove limit"
                    onClick={() => setDraft(rows => rows.filter((_, idx) => idx !== i))}
                    className="shrink-0 rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <select
                    aria-label="Max or min"
                    value={row.bound}
                    onChange={e => updateRow(i, { bound: e.target.value as WeatherLimitBound })}
                    disabled={def.bounds.length === 1}
                    className="rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
                  >
                    {def.bounds.map(b => <option key={b} value={b}>{b === 'max' ? 'Max' : 'Min'}</option>)}
                  </select>
                  <input
                    aria-label="Limit"
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={row.threshold}
                    onChange={e => updateRow(i, { threshold: e.target.value })}
                    className="w-20 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
                  />
                  <span className="text-neutral-500">{def.unit}</span>
                  {def.kind === 'forecast' && (
                    <>
                      <span className="text-neutral-500">over next</span>
                      <input
                        aria-label="Look ahead (hours)"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={168}
                        value={row.window_hours}
                        onChange={e => updateRow(i, { window_hours: e.target.value })}
                        className="w-16 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
                      />
                      <span className="text-neutral-500">h</span>
                    </>
                  )}
                </div>
                {def.hint && <p className="text-xs text-neutral-500">{def.hint}</p>}
              </div>
            );
          })}

          <Button variant="outline" size="sm" onClick={addRow}>
            <Plus className="h-4 w-4" />
            Add limit
          </Button>

          {draft.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-neutral-200 pt-3">
              <input
                aria-label="Preset name"
                placeholder="Save these as a preset, e.g. Weed spray"
                value={presetName}
                onChange={e => setPresetName(e.target.value)}
                className="min-w-0 flex-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm"
              />
              <Button variant="ghost" size="sm" onClick={saveAsPreset}>Save preset</Button>
            </div>
          )}

          {presetMessage && <p className="text-xs text-green-700">{presetMessage}</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save limits'}
            </Button>
          </div>
        </div>
      )}

      {/* ── History ─────────────────────────────────────────────────────── */}
      {!editing && (data?.breaches.length ?? 0) > 0 && (
        <div className="border-t border-neutral-200 pt-2">
          <button
            type="button"
            onClick={() => setShowHistory(v => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-800"
          >
            <History className="h-3.5 w-3.5" />
            {showHistory ? 'Hide' : 'Show'} times outside limits ({data!.breaches.length})
          </button>
          {showHistory && (
            <ul className="mt-2 space-y-1.5">
              {data!.breaches.map(b => (
                <li key={b.id} className="text-xs text-neutral-600">
                  <span className="font-medium text-neutral-800">
                    {b.label} {formatWeatherValue(b.observed_value, b.unit)}
                  </span>{' '}
                  ({describeLimit(b)}) · {formatDateTimeInTz(b.opened_at, timezone)}
                  {b.cleared_at ? ` – ${formatDateTimeInTz(b.cleared_at, timezone)}` : ' · ongoing'}
                  {b.phase === 'planned' && ' · forecast'}
                  {b.acknowledged_at && ` · started anyway by ${b.acknowledged_by_name ?? 'operator'}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default WeatherLimitsCard;
